import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import AvailabilityMatrix from "@/components/AvailabilityMatrix";
import NotifyMeButton from "@/components/NotifyMeButton";
import FilterBar from "@/components/FilterBar";
import LibraryActions from "@/components/LibraryActions";
import RatingWidget from "@/components/RatingWidget";
import FanBoard from "@/components/FanBoard";
import CriticVideos from "@/components/CriticVideos";
import GlobalAvailability from "@/components/GlobalAvailability";
import { fetchCountries, fetchLanguages, fetchPlatforms, fetchTitle } from "@/lib/api";
import { useLocale } from "@/lib/locale";
import { ArrowLeft, Clock, Calendar, Star, Loader2, Film, Ticket } from "lucide-react";

export default function TitleDetail() {
  const { id } = useParams();
  const { t, country: userCountry } = useLocale();
  const [title, setTitle] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [filters, setFilters] = useState({ platform: "", country: "", language: "" });

  useEffect(() => {
    setTitle(null);
    Promise.all([fetchTitle(id), fetchPlatforms(), fetchCountries(), fetchLanguages()])
      .then(([tt, p, c, l]) => { setTitle(tt); setPlatforms(p); setCountries(c); setLanguages(l); })
      .catch(() => setTitle({ notFound: true }));
  }, [id]);

  const turkishAvailable = useMemo(() =>
    title?.availability?.some((a) => a.country === "TR"), [title]);

  const platformsMap = useMemo(
    () => Object.fromEntries(platforms.map((p) => [p.id, p])), [platforms]
  );

  const filtered = useMemo(() => {
    if (!title?.availability) return [];
    return title.availability.filter((a) => {
      if (filters.platform && a.platform !== filters.platform) return false;
      if (filters.country && a.country !== filters.country) return false;
      if (filters.language && !a.audio.includes(filters.language) && !a.subtitles.includes(filters.language)) return false;
      return true;
    });
  }, [title, filters]);

  const ticketUrl = useMemo(() => {
    if (!title || title.type !== "movie") return null;
    const year = parseInt(title.year, 10);
    const currentYear = new Date().getFullYear();
    if (!year || year < currentYear - 1) return null;
    const base = userCountry === "TR"
      ? `https://www.biletix.com/search/TURKIYE/tr#q=${encodeURIComponent(title.title)}&aff=moviohub`
      : `https://www.fandango.com/search?q=${encodeURIComponent(title.title)}&aff=moviohub`;
    return base;
  }, [title, userCountry]);

  if (!title) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber" /></div>;
  }

  if (title.notFound) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="font-display text-4xl text-white mb-3">Title not found</h1>
          <Link to="/" className="text-amber font-heading">← {t("back")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />

      <div className="relative h-[55vh] min-h-[420px] overflow-hidden -mt-px">
        <img src={title.backdrop} alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 h-full flex items-end pb-10">
          <Link
            to="/"
            className="absolute top-6 left-6 inline-flex items-center gap-2 text-zinc-300 hover:text-amber font-heading text-sm"
            data-testid="back-to-search"
          >
            <ArrowLeft className="h-4 w-4" /> {t("back")}
          </Link>

          <div className="flex flex-col md:flex-row gap-8 items-end">
            <img
              src={title.poster}
              alt={title.title}
              className="hidden md:block w-56 rounded-md border border-white/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]"
            />
            <div className="max-w-3xl">
              <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-amber font-heading mb-3">
                <span>{title.type === "tv" ? t("series") : t("feature_film")}</span>
                {title.genres.slice(0, 3).map((g) => (
                  <span key={g} className="text-zinc-500">· {g}</span>
                ))}
              </div>
              <h1 className="font-display text-5xl sm:text-6xl text-white leading-[1.02]" data-testid="title-name">
                {title.title}
              </h1>
              <p className="mt-3 italic text-zinc-400 font-display">{title.tagline}</p>
              <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-zinc-300 font-heading">
                <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4 text-amber" /> {title.year}</span>
                {title.runtime ? <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-amber" /> {title.runtime} min</span> : null}
                <span className="inline-flex items-center gap-1.5"><Star className="h-4 w-4 text-amber fill-amber" /> {title.rating}</span>
                {title.director && <span className="text-zinc-500">Dir. {title.director}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-12 space-y-12">
        <section className="grid md:grid-cols-[1fr_auto] gap-6 items-start glass rounded-md p-6">
          <div>
            <p className="text-zinc-300 font-body leading-relaxed max-w-3xl">{title.overview}</p>
            <div className="mt-4">
              <LibraryActions titleId={title.id} />
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <NotifyMeButton title={title} alreadyTurkishDub={turkishAvailable} />
            {ticketUrl && (
              <a
                href={ticketUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-amber/40 bg-amber/10 text-amber hover:bg-amber/20 text-sm font-heading transition"
                data-testid="buy-tickets-btn"
              >
                <Ticket className="h-4 w-4" /> {t("buy_tickets")}
              </a>
            )}
            <div className="w-full max-w-xs"><RatingWidget titleId={title.id} /></div>
          </div>
        </section>

        <GlobalAvailability
          availability={title.availability}
          userCountry={userCountry}
          platformsMap={platformsMap}
        />

        <section>
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-amber font-heading mb-1">Control Room</div>
              <h2 className="font-display text-3xl text-white">{t("where_to_watch")}</h2>
            </div>
            <div className="text-sm text-zinc-500 font-heading">{filtered.length} of {title.availability.length} listings</div>
          </div>
          <div className="mb-4">
            <FilterBar
              platforms={platforms}
              countries={countries}
              languages={languages}
              filters={filters}
              setFilters={setFilters}
              advanced={false}
            />
          </div>
          <AvailabilityMatrix
            availability={filtered}
            platforms={platforms}
            countries={countries}
            languages={languages}
          />
        </section>

        <section>
          <div className="text-[11px] uppercase tracking-[0.25em] text-amber font-heading mb-1">Cuts &amp; Versions</div>
          <h2 className="font-display text-3xl text-white mb-5">{t("different_versions")}</h2>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="versions-grid">
            {title.versions.map((v, i) => (
              <div key={i} className="border border-white/10 rounded-md p-5 bg-obsidian-surface/60">
                <div className="flex items-center gap-2 text-amber font-heading text-sm mb-2">
                  <Film className="h-4 w-4" /> {v.name}
                </div>
                <div className="font-display text-xl text-white">{v.runtime} min</div>
                <p className="text-sm text-zinc-400 mt-2 font-body">{v.notes}</p>
              </div>
            ))}
          </div>
        </section>

        <CriticVideos titleId={title.id} />
        <FanBoard titleId={title.id} />
      </main>
    </div>
  );
}
